import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Check, Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg, fmtINR } from "@/lib/api";

async function sha256(value) {
  const bytes = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(value));
  return [...new Uint8Array(bytes)].map((b) => b.toString(16).padStart(2, "0")).join("");
}

function loadRazorpay() {
  return new Promise((resolve) => {
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

export function PlanCards({ selected, onSelect, testidPrefix = "plan" }) {
  const { data: plans } = useQuery({ queryKey: ["plans"], queryFn: async () => (await api.get("/plans")).data });
  return (
    <div className="grid gap-4 sm:grid-cols-3">
      {(plans || []).map((p) => (
        <button key={p.id} type="button" data-testid={`${testidPrefix}-${p.slug}`} onClick={() => onSelect(p)}
          className={`rounded-2xl border-2 p-5 text-left transition-all ${selected?.id === p.id ? "border-[#FF6B00] bg-orange-50 shadow-lg" : "border-slate-200 bg-white hover:border-orange-300"}`}>
          <p className="text-xs font-bold uppercase tracking-widest text-[#1D5BD8]">{p.name}</p>
          <p className="mt-2 text-3xl font-extrabold text-[#0F2440]" style={{ fontFamily: "Outfit, sans-serif" }}>{fmtINR(p.price)}</p>
          <p className="text-[11px] font-semibold text-slate-500">ONE-TIME · {p.max_products} products</p>
          <ul className="mt-4 space-y-1.5">
            {(p.features || []).slice(0, 6).map((f) => (
              <li key={f} className="flex items-start gap-1.5 text-[11px] text-slate-600">
                <Check size={12} className="mt-0.5 shrink-0 text-[#138808]" /> {f}
              </li>
            ))}
          </ul>
          <p className="mt-3 rounded-lg bg-slate-50 px-2.5 py-1.5 text-[10px] text-slate-500">
            Year 1 service: <b className="text-[#138808]">FREE</b> · From year 2: ₹{p.annual_service_fee?.toLocaleString("en-IN")}/year
          </p>
        </button>
      ))}
    </div>
  );
}

export default function PlanGate({ onActivated }) {
  const [selected, setSelected] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const pay = async () => {
    if (!selected) return;
    setBusy(true);
    setError("");
    try {
      const { data: co } = await api.post("/agent/plan/checkout", { plan_id: selected.id, type: "plan" });
      if (co.mode === "mock") {
        const paymentId = `pay_mock_${crypto.randomUUID().slice(0, 8)}`;
        const signature = await sha256(`${co.order_id}|${paymentId}`);
        await api.post("/agent/plan/verify", { purchase_id: co.purchase_id, razorpay_order_id: co.order_id, razorpay_payment_id: paymentId, razorpay_signature: signature });
        toast.success(`${selected.name} plan activated — welcome aboard! (test payment)`);
        onActivated?.();
        return;
      }
      const loaded = await loadRazorpay();
      if (!loaded) throw new Error("Could not load payment gateway");
      const rzp = new window.Razorpay({
        key: co.key_id, amount: co.amount, currency: "INR", name: "DropPartner",
        description: co.description, order_id: co.order_id,
        handler: async (resp) => {
          try {
            await api.post("/agent/plan/verify", { purchase_id: co.purchase_id, ...resp });
            toast.success(`${selected.name} plan activated — welcome aboard!`);
            onActivated?.();
          } catch (e) {
            setError(errMsg(e));
          }
        },
        modal: { ondismiss: () => setError("Payment not completed — your plan activates only after successful payment.") },
      });
      rzp.open();
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto max-w-4xl py-8" data-testid="plan-gate">
      <div className="mb-8 text-center">
        <h1 className="text-2xl font-extrabold tracking-tight text-[#0F2440] sm:text-3xl" style={{ fontFamily: "Outfit, sans-serif" }}>
          Choose your Dropship plan
        </h1>
        <p className="mx-auto mt-2 max-w-md text-sm text-slate-500">
          One-time payment. No monthly subscription. First year service charge FREE — ₹1,500/year only from year 2.
        </p>
      </div>
      <PlanCards selected={selected} onSelect={setSelected} testidPrefix="gate-plan" />
      {error && <p data-testid="plan-error" className="mt-4 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-600">{error}</p>}
      <div className="mt-6 text-center">
        <button data-testid="plan-pay-btn" onClick={pay} disabled={!selected || busy}
          className="inline-flex items-center gap-2 rounded-full bg-[#FF6B00] px-10 py-4 text-sm font-bold text-white shadow-[0_10px_30px_rgba(255,107,0,0.35)] transition-all hover:scale-105 disabled:opacity-40">
          {busy ? <Loader2 size={16} className="animate-spin" /> : <ShieldCheck size={16} />}
          {busy ? "Processing…" : selected ? `PAY ${fmtINR(selected.price)} & ACTIVATE` : "SELECT A PLAN"}
        </button>
        <p className="mt-3 text-[11px] text-slate-400">Secure payment via Razorpay. Plan activates only after verified payment.</p>
      </div>
    </div>
  );
}
